import { useCallback, useState } from "react";
import { Tooltip } from "@mui/material";
import { useHasScope } from "../../../utils/Permission";
import ConfirmModal from "../../global/ConfirmModal";
import Notification from "../../global/Notification";
import SyncAllButton from "../SyncAllButton";

export type EntityLabel = { one: string; many: string };

type Props = {
    selectedCount: number;
    entityLabel: EntityLabel;
    onForceSyncSelected?: () => Promise<string>;
    onClearSelection: () => void;
    isSyncing?: boolean;
};

export default function ForceSyncSelectedButton({ selectedCount, entityLabel, onForceSyncSelected, onClearSelection, isSyncing }: Props) {
    const canSyncSelection = useHasScope(['stores:create', 'write-force-sync'], true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isConfirmOpen, setConfirmOpen] = useState(false);

    const dismissError = useCallback(() => setErrorMessage(null), []);

    if (!onForceSyncSelected || !canSyncSelection) return null;

    const handleConfirm = async () => {
        setConfirmOpen(false);
        setErrorMessage(null);
        const message = await onForceSyncSelected();
        if (message) setErrorMessage(message);
    };

    const button = (
        <SyncAllButton
            className="force-sync-btn"
            label={isSyncing ? "Syncing…" : selectedCount > 0 ? `Force Sync ${selectedCount}` : "Force Sync"}
            syncing={isSyncing}
            disabled={selectedCount === 0}
            onClick={() => setConfirmOpen(true)}
        />
    );

    return (
        <div className="force-sync-selection">
            {selectedCount === 0
                ? <Tooltip title={`Select ${entityLabel.many} to sync`} placement="top"><span>{button}</span></Tooltip>
                : button}
            {selectedCount > 0 && (
                <button type="button" className="clear-selection" onClick={onClearSelection}>
                    Clear selection
                </button>
            )}
            <ConfirmModal
                open={isConfirmOpen}
                title={`Force sync ${selectedCount} ${selectedCount === 1 ? entityLabel.one : entityLabel.many}?`}
                message="This re-sends the selected records to Shopify, whatever state they are in. It can't be stopped once it starts."
                confirmLabel="Force sync"
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
            />
            <Notification message={errorMessage} type="error" onClose={dismissError} />
        </div>
    );
}
